import React, { useEffect, useState } from 'react';
import Calendar from 'react-calendar';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import { complianceService, partnerService, orderService, landlordApi } from '../../services/api';
import './styles/Calendar.css';

interface CalendarEvent {
    id: string;
    date: Date;
    type: 'red' | 'orange' | 'green' | 'blue';
    title: string;
    property: string;
    eventType: 'certificate' | 'order';
    orderId?: string;
    status?: string;
    certificateType?: string;
    partnerName?: string;
}

interface Partner {
    id: string;
    businessName: string;
    partnerType: string;
    averageRating?: number;
}

export default function ComplianceCalendar() {
    const [date, setDate] = useState<Date>(new Date());
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [partners, setPartners] = useState<Partner[]>([]);
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [selectedDateEvents, setSelectedDateEvents] = useState<CalendarEvent[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Use Promise.allSettled so one failure doesn't crash the whole calendar
                const [certResult, partnerResult, ordersResult] = await Promise.allSettled([
                    complianceService.getLandlordCertificates(),
                    // api/partners/all is dead (routes/partners.js is never
                    // mounted); super-admin is the route that answers.
                    partnerService.listVerified().catch(() => ({ data: { partners: [] } })),
                    orderService.listOrders()
                ]);

                // Extract certificates (graceful)
                const certs = certResult.status === 'fulfilled'
                    ? (certResult.value.data?.data || certResult.value.data || [])
                    : [];

                // Extract partners (graceful — may 404 if no partners registered)
                let verifiedPartners: Partner[] = [];
                if (partnerResult.status === 'fulfilled') {
                    const pData = partnerResult.value.data;
                    verifiedPartners = pData?.partners || pData?.data || [];
                }
                setPartners(verifiedPartners);

                // Extract orders (graceful)
                const orders = ordersResult.status === 'fulfilled'
                    ? (ordersResult.value.data?.data || ordersResult.value.data || [])
                    : [];

                if (certResult.status === 'rejected') {
                    console.warn('[Calendar] Certificates fetch failed:', certResult.reason);
                }
                if (partnerResult.status === 'rejected') {
                    console.warn('[Calendar] Partners fetch failed (non-fatal):', partnerResult.reason);
                }
                if (ordersResult.status === 'rejected') {
                    console.warn('[Calendar] Orders fetch failed:', ordersResult.reason);
                }

                const now = new Date();

                // Map certificate expiration events
                const certEvents: CalendarEvent[] = (Array.isArray(certs) ? certs : []).map((c: any) => {
                    const expiry = new Date(c.expires);
                    const diffTime = expiry.getTime() - now.getTime();
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                    let type: 'red' | 'orange' | 'green' = 'green';
                    if (diffDays <= 0) type = 'red';
                    else if (diffDays <= 30) type = 'orange';

                    return {
                        id: c.id,
                        date: expiry,
                        type,
                        title: `${c.type} Expires`,
                        property: `Property ID: ${c.propertyId}`,
                        eventType: 'certificate' as const,
                        certificateType: c.type
                    };
                });

                // Map order events — include ALL statuses (even clashing dates)
                const orderEvents: CalendarEvent[] = (Array.isArray(orders) ? orders : [])
                    .filter((o: any) => o.dateRange?.startDate || o.createdAt)
                    .map((o: any) => {
                        const orderDate = o.dateRange?.startDate ? new Date(o.dateRange.startDate) : new Date(o.createdAt);

                        let type: 'red' | 'orange' | 'green' | 'blue' = 'blue';
                        if (o.status === 'pending') type = 'blue';
                        else if (o.status === 'assigned') type = 'orange';
                        else if (o.status === 'in_progress') type = 'orange';
                        else if (o.status === 'completed') type = 'green';
                        else if (o.status === 'cancelled') type = 'red';

                        return {
                            id: o.id,
                            date: orderDate,
                            type,
                            title: `${o.certificateType} Order`,
                            property: `Property: ${o.propertyId}`,
                            eventType: 'order' as const,
                            orderId: o.id,
                            status: o.status,
                            certificateType: o.certificateType,
                            partnerName: o.partnerName || 'Unassigned'
                        };
                    });

                // Combine and sort all events
                const allEvents = [...certEvents, ...orderEvents].sort((a, b) => a.date.getTime() - b.date.getTime());
                setEvents(allEvents);
            } catch (error) {
                console.error("Failed to fetch calendar events", error);
            } finally {
                setLoadingEvents(false);
            }
        };

        fetchData();
    }, []);

    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

    // Auto-select first order when date changes
    useEffect(() => {
        const daysEvents = events.filter(e =>
            e.date.getDate() === date.getDate() &&
            e.date.getMonth() === date.getMonth() &&
            e.date.getFullYear() === date.getFullYear()
        );
        setSelectedDateEvents(daysEvents);
        
        const firstOrder = daysEvents.find(e => e.eventType === 'order');
        setSelectedOrderId(firstOrder ? firstOrder.orderId || null : null);
    }, [date, events]);

    const handleAssignJob = async (partnerId: string) => {
        if (!selectedOrderId) {
            alert('Please select an Order event first.');
            return;
        }

        try {
            await orderService.assignPartner(selectedOrderId, partnerId);
            alert('Job successfully assigned to partner!');
            // Refresh logic: we can rely on page reload or simple state mutation, but reload is safest for now
            window.location.reload();
        } catch (err: any) {
            alert('Assignment failed: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleShareToNetwork = async (orderId: string, property: string, certType: string) => {
        // Here we call the new backend endpoint to share this specific job
        try {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('idToken');
            const headers = { Authorization: `Bearer ${token}` };
            await landlordApi.post(`api/orders/${orderId}/share-network`, {}, { headers });
            alert(`Job for ${certType} successfully shared to the Trade Partner Telegram Network for quoting!`);
        } catch (err: any) {
            alert('Failed to share job: ' + (err.response?.data?.message || err.message));
        }
    };

    const tileContent = ({ date, view }: { date: Date, view: string }) => {
        if (view === 'month') {
            const daysEvents = events.filter(e =>
                e.date.getDate() === date.getDate() &&
                e.date.getMonth() === date.getMonth() &&
                e.date.getFullYear() === date.getFullYear()
            );

            if (daysEvents.length > 0) {
                return (
                    <div className="calendar-tile-content">
                        <div className="flex gap-1 justify-center flex-wrap">
                            {daysEvents.map((e, idx) => (
                                <span
                                    key={idx}
                                    className={`event-dot ${e.type} ${e.eventType}`}
                                    title={`${e.title} - ${e.property} (${e.eventType})`}
                                ></span>
                            ))}
                        </div>
                    </div>
                );
            }
        }
        return null;
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans flex flex-col">
            <Navbar />

            <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900">Trade Partner Compliance Calendar</h2>
                    <p className="mt-1 text-sm text-gray-500">Track expiring certificates, manage partner compliance, and assign service jobs.</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                    {/* Left Column: Calendar */}
                    <div className="lg:col-span-2">
                        <div className="bg-white shadow rounded-lg p-6">
                            {loadingEvents ? (
                                <div className="h-96 flex items-center justify-center">
                                    <i className="fas fa-spinner fa-spin text-green-600 text-3xl"></i>
                                </div>
                            ) : (
                                <Calendar
                                    onChange={(value) => setDate(value as Date)}
                                    value={date}
                                    tileContent={tileContent}
                                    className="border-0 w-full"
                                />
                            )}
                        </div>

                        {/* Selected Date Details */}
                        <div className="mt-6 bg-white shadow rounded-lg p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                Events for {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </h3>
                            {selectedDateEvents.length > 0 ? (
                                <ul className="divide-y divide-gray-200">
                                    {selectedDateEvents.map(event => {
                                        const isSelectedOrder = event.orderId === selectedOrderId;
                                        return (
                                        <li 
                                            key={event.id} 
                                            className={`py-4 px-2 flex flex-col sm:flex-row items-start justify-between rounded-lg transition-colors ${isSelectedOrder ? 'bg-blue-50 ring-1 ring-blue-500' : 'hover:bg-gray-50'}`}
                                            onClick={() => { if(event.eventType === 'order') setSelectedOrderId(event.orderId || null) }}
                                        >
                                            <div className="flex flex-1">
                                                <span className={`h-3 w-3 mt-1.5 rounded-full mr-3 shrink-0 bg-${event.type === 'red' ? 'red' : event.type === 'orange' ? 'yellow' : event.type === 'blue' ? 'blue' : 'green'}-500`}></span>
                                                <div className="flex-1 cursor-pointer">
                                                    <div className="flex items-center gap-3">
                                                        <p className="text-sm font-bold text-gray-900">{event.title}</p>
                                                        {event.eventType === 'order' && (
                                                            <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded ${
                                                                event.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                                                                event.status === 'assigned' || event.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                                                                event.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                                'bg-red-100 text-red-800'
                                                            }`}>
                                                                {event.status}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-gray-600 mt-1 font-mono">{event.property}</p>
                                                    {event.eventType === 'order' && event.partnerName && (
                                                        <p className="text-xs text-gray-500 mt-1"><i className="fas fa-hard-hat mr-1"></i> Partner: {event.partnerName}</p>
                                                    )}
                                                </div>
                                            </div>
                                            {event.eventType === 'order' && event.status === 'pending' && (
                                                <div className="mt-3 sm:mt-0 sm:ml-4 flex gap-2">
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); handleShareToNetwork(event.orderId!, event.property, event.certificateType!); }}
                                                        className="px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded hover:bg-blue-700 transition"
                                                    >
                                                        <i className="fab fa-telegram-plane mr-1"></i> Invite Quotes
                                                    </button>
                                                </div>
                                            )}
                                        </li>
                                    )})}
                                </ul>
                            ) : (
                                <p className="text-sm text-gray-500 italic">No events scheduled for this day.</p>
                            )}
                        </div>
                    </div>
                    {/* Right Column: Reports & Upcoming */}
                    <div className="lg:col-span-1 space-y-6">

                        {/* Trade Partner Network Section */}
                        <div className="bg-white shadow rounded-lg p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900">Trade Partner Network</h3>
                                <i className="fas fa-handshake text-green-600"></i>
                            </div>
                            <p className="text-xs text-gray-500 mb-4">Assign jobs to verified partners to resolve upcoming compliance issues.</p>
                            <ul className="space-y-3 max-h-60 overflow-y-auto">
                                {partners.length > 0 ? (
                                    partners.map((partner) => (
                                        <li key={partner.id} className="flex flex-col p-3 bg-green-50 rounded-md border border-green-100 hover:border-green-300 transition-colors">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900">{partner.businessName}</p>
                                                    <p className="text-xs text-gray-600 italic">{partner.partnerType}</p>
                                                </div>
                                                <div className="flex text-yellow-500 text-xs">
                                                    <i className="fas fa-star"></i>
                                                    <span className="ml-1 text-gray-700 font-bold">{partner.averageRating || 4.8}</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleAssignJob(partner.id)}
                                                className="mt-2 w-full py-1.5 bg-green-600 text-white text-xs font-bold rounded hover:bg-green-700 transition"
                                            >
                                                Assign to Job
                                            </button>
                                        </li>
                                    ))
                                ) : (
                                    <div className="text-center py-4 bg-gray-50 rounded italic text-xs text-gray-500">
                                        No partners available in your area.
                                        <br />
                                        <a href="/invite-partner" className="text-green-600 underline">Invite a Partner</a>
                                    </div>
                                )}
                            </ul>
                        </div>

                        {/* Reports Section */}
                        <div className="bg-white shadow rounded-lg p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900">Compliance Reports</h3>
                                <i className="fas fa-file-pdf text-gray-400"></i>
                            </div>
                            <ul className="space-y-3">
                                {[
                                    { id: '1', month: 'November', year: 2025, url: '#', status: 'READY' },
                                    { id: '2', month: 'October', year: 2025, url: '#', status: 'READY' }
                                ].map((report) => (
                                    <li key={report.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-8 w-8 rounded bg-red-100 flex items-center justify-center text-red-600">
                                                <i className="fas fa-file-pdf"></i>
                                            </div>
                                            <div className="ml-3">
                                                <p className="text-sm font-medium text-gray-900">{report.month} {report.year}</p>
                                                <p className="text-xs text-gray-500">{report.status === 'READY' ? 'Available' : 'Generating...'}</p>
                                            </div>
                                        </div>
                                        <button className="text-gray-400 hover:text-green-600 transition-colors">
                                            <i className="fas fa-download"></i>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Legend */}
                        <div className="bg-white shadow rounded-lg p-6">
                            <h3 className="text-sm font-medium text-gray-900 mb-3">Legend</h3>
                            <div className="space-y-2">
                                <p className="text-xs font-semibold text-gray-500 mb-2">Certificate Events:</p>
                                <div className="flex items-center text-sm text-gray-600">
                                    <span className="h-3 w-3 rounded-full bg-red-500 mr-2"></span>
                                    Expired / Expiring Today
                                </div>
                                <div className="flex items-center text-sm text-gray-600">
                                    <span className="h-3 w-3 rounded-full bg-yellow-500 mr-2"></span>
                                    Expiring in 30 Days
                                </div>
                                <div className="flex items-center text-sm text-gray-600">
                                    <span className="h-3 w-3 rounded-full bg-green-500 mr-2"></span>
                                    Valid ({'>'} 30 Days)
                                </div>
                                <div className="border-t border-gray-200 my-2"></div>
                                <p className="text-xs font-semibold text-gray-500 mb-2">Order Events:</p>
                                <div className="flex items-center text-sm text-gray-600">
                                    <span className="h-3 w-3 rounded-full bg-blue-500 mr-2"></span>
                                    Pending / Assigned
                                </div>
                                <div className="flex items-center text-sm text-gray-600">
                                    <span className="h-3 w-3 rounded-full bg-green-500 mr-2"></span>
                                    Completed
                                </div>
                                <div className="flex items-center text-sm text-gray-600">
                                    <span className="h-3 w-3 rounded-full bg-red-500 mr-2"></span>
                                    Cancelled
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}
